import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useUploadConceptGroupFileSync,
  useValidatePreviewName,
} from '@verantos-dev/feature-epi-chat';

import { prefetchDraftReviewData } from '../../../lib/api/prefetchDraftReviewData';
import { projectPageKeys } from '../../../lib/api/queryKeys';

/** Mirrors ConceptGroupDraftStepOne's debounced duplicate-name check. */
export type ConceptGroupNameCheckStatus = 'idle' | 'checking' | 'available' | 'taken';

/** Parity with ConceptGroupDraftStepOne's step-1 counter. Soft cap: typing
 *  (or dropping text) past it is allowed but errors the field and blocks
 *  Create — a hard `maxLength` silently truncates and drag-drop bypasses it. */
export const CONCEPT_GROUP_NAME_MAX_LENGTH = 100;

export interface CreatedConceptGroupDraft {
  previewId: string;
  conceptGroupId: string | null;
}

interface UseCreateConceptGroupModalArgs {
  projectId: string;
  onClose: () => void;
  /** Fired with the minted draft's ids before close so the caller can route
   *  into the draft review page. */
  onCreated: (created: CreatedConceptGroupDraft) => void;
}

/**
 * Behaviour for the gen-3 inline "Create concept group" modal (replaces the
 * in-page stepper entry for the artifact list's Create menu): name entry with
 * live duplicate check, file pick, and the sync upload that mints the draft.
 * Intl-free so the view owns copy and this stays unit-testable without
 * rendering the dialog — same split as useCreateAnalyticalDatasetDialog.
 *
 */
export const useCreateConceptGroupModal = ({
  projectId,
  onClose,
  onCreated,
}: UseCreateConceptGroupModalArgs) => {
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [searchParams] = useSearchParams();
  // The upload completes synchronously; when a conversation is attached
  // (?chat=) the file chip + draft card mirror into it silently (OR-6288 W6).
  const chatSessionId = searchParams.get('chat');

  const { mutate: uploadFile, isPending, error, reset } = useUploadConceptGroupFileSync();
  const queryClient = useQueryClient();
  // Keeps the dialog in its submitting state while the destination's data is
  // prefetched post-upload — the review then renders with no skeleton flash.
  const [isFinalizing, setIsFinalizing] = useState(false);

  // Debounced duplicate-name check (parity with ConceptGroupDraftStepOne):
  // spinner while checking, a taken name blocks Create, and Create is ALSO
  // blocked while the check is in flight (``checking`` — set for the whole
  // debounce + request window) so a name can't be submitted before it resolves.
  // Fails open on a check error — the backend re-validates on submit.
  const { mutateAsync: validatePreviewName } = useValidatePreviewName();
  const [nameStatus, setNameStatus] = useState<ConceptGroupNameCheckStatus>('idle');
  const trimmedName = name.trim();
  useEffect(() => {
    if (!trimmedName) {
      setNameStatus('idle');
      return;
    }
    setNameStatus('checking');
    const handle = setTimeout(() => {
      validatePreviewName({ conceptGroupName: trimmedName, projectId })
        .then((isAvailable) => setNameStatus(isAvailable ? 'available' : 'taken'))
        .catch(() => setNameStatus('idle'));
    }, 400);
    return () => clearTimeout(handle);
  }, [trimmedName, projectId, validatePreviewName]);

  // The counter reflects the raw value, so the limit does too.
  const isNameTooLong = name.length > CONCEPT_GROUP_NAME_MAX_LENGTH;

  // A picked file alone isn't enough — the name is required here (unlike the
  // draft review's step 1, where the backend derives one).
  const isValidToSubmit =
    Boolean(file) &&
    Boolean(trimmedName) &&
    !isNameTooLong &&
    nameStatus !== 'taken' &&
    nameStatus !== 'checking';

  const onSubmit = useCallback(
    (fileOverride?: File) => {
      // ``fileOverride`` lets the error-step "Choose a different file" resubmit
      // with the freshly picked file without waiting on the ``file`` setState.
      const fileToUpload = fileOverride ?? file;
      // Guard mirrors isValidToSubmit — never submit while the name is still
      // validating (checking) or a duplicate (taken).
      if (
        !fileToUpload ||
        !trimmedName ||
        isNameTooLong ||
        nameStatus === 'taken' ||
        nameStatus === 'checking'
      )
        return;
      uploadFile(
        { projectId, file: fileToUpload, name: trimmedName, sessionId: chatSessionId },
        {
          onSuccess: async (created) => {
            // Warm the review's queries before navigating so the destination
            // renders immediately instead of flashing skeletons (prefetch
            // failures fall back to the destination fetching normally).
            setIsFinalizing(true);
            // The upload just minted a Draft indexed under this project — refresh
            // the artifact list (+ activity) so it shows without a manual refetch.
            queryClient.invalidateQueries({
              queryKey: projectPageKeys.artifactsForProject(projectId),
            });
            queryClient.invalidateQueries({
              queryKey: projectPageKeys.activityForProject(projectId),
            });
            await prefetchDraftReviewData(queryClient, {
              conceptGroupId: created.conceptGroupId,
              previewId: created.previewId,
            });
            onCreated({ previewId: created.previewId, conceptGroupId: created.conceptGroupId });
            onClose();
          },
        }
      );
    },
    [
      file,
      trimmedName,
      isNameTooLong,
      nameStatus,
      uploadFile,
      projectId,
      chatSessionId,
      queryClient,
      onCreated,
      onClose,
    ]
  );

  const closeDialog = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return {
    name,
    setName,
    file,
    setFile,
    nameStatus,
    isNameTooLong,
    onSubmit,
    closeDialog,
    isPending: isPending || isFinalizing,
    submittingError: error ? { message: error.message } : undefined,
    isValidToSubmit,
  };
};
